import { makeAutoObservable, runInAction, toJS } from 'mobx';
import cloneDeep from 'lodash.clonedeep';

import { AuthStore } from 'stores/users/AuthStore';
import { LoadStatus } from 'utils/types';
import { serverUri } from 'utils/config';
import { underwritingAccessLevelOptions } from 'utils/options';

import { nullsToUndefined } from 'utils/nullsToUndefined';
import {
  UnderwritingAccessLevel,
  SharedAccessInput,
  SharedWithUser,
  UserPreviewOutput,
} from 'components/underwriting/types';

export class CancelShareEvent extends Event {
  static readonly eventType = 'cancelShare';
  constructor() {
    super(CancelShareEvent.eventType);
  }
}

export class ShareAccessEvent extends Event {
  static readonly eventType = 'shareAccess';
  constructor() {
    super(ShareAccessEvent.eventType);
  }
}

export class UnderwritingAccessStore {
  readonly events = new EventTarget();

  closing = false;
  saveStatus: LoadStatus = 'none';
  usersPreviewsStatus: LoadStatus = 'none';
  usersPreviews: UserPreviewOutput[] = [];
  sharedWith: SharedWithUser[] = [];
  setSharedWith: (id: string, sharedWith: SharedWithUser[]) => void;

  constructor(
    private readonly authStore: AuthStore,
    private readonly underwritingId: string,
    sharedWith: SharedWithUser[] = [],
    setSharedWith: (id: string, sharedWith: SharedWithUser[]) => void = () => {}
  ) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.sharedWith = sharedWith ? cloneDeep(toJS(sharedWith)) : [];
    this.setSharedWith = setSharedWith;

    this.getUsersPreviews();
  }

  async getUsersPreviews() {
    this.usersPreviewsStatus = 'loading';
    this.usersPreviews = [];

    try {
      const response = await this.authStore.fetchWithAuth(`${serverUri}/share-access/users`, {
        method: 'GET',
      });

      if (!response.ok) throw new Error('Error getting accounts');

      const output: UserPreviewOutput[] = (await response.json())?.users || [];
      runInAction(() => {
        this.usersPreviewsStatus = 'success';
        this.usersPreviews = output;
      });
    } catch (err) {
      runInAction(() => {
        this.usersPreviewsStatus = 'error';
      });
    }
  }

  startClosing = () => {
    this.closing = true;
  };

  handleClosed = () => {
    this.closeCallback();
  };

  shareAccessWith(user: UserPreviewOutput, accessLevel: UnderwritingAccessLevel = 'edit') {
    const isAlreadyShared = this.sharedWith.some(({ email }) => email?.toLowerCase() === user.email?.toLowerCase());
    if (!isAlreadyShared) {
      this.sharedWith.push({ ...user, accessLevel });
    }
  }

  changeUserAccessLevel(email: string, accessLevel: UnderwritingAccessLevel) {
    if (!accessLevel || !Object.keys(underwritingAccessLevelOptions).includes(accessLevel)) {
      return;
    }

    const targetUserIndex = this.sharedWith.findIndex((user) => email?.toLowerCase() === user.email?.toLowerCase());
    if (targetUserIndex !== -1) {
      this.sharedWith[targetUserIndex] = { ...this.sharedWith[targetUserIndex], accessLevel };
    }
  }

  private closeCallback = () => {
    this.events.dispatchEvent(new CancelShareEvent());
  };

  saveModal = async () => {
    this.saveStatus = 'loading';
    const dto: SharedAccessInput = {
      sharedWith: this.sharedWith
        .filter(({ accessLevel }) => accessLevel !== 'removeAccess')
        .map(({ email, accessLevel }) => ({ email, accessLevel })),
    };
    try {
      const response = await this.authStore.fetchWithAuth(`${serverUri}/underwriting/${this.underwritingId}/share`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dto),
      });

      if (!response.ok) {
        throw new Error();
      }

      const sharedWith: SharedWithUser[] = nullsToUndefined(await response.json());
      runInAction(() => {
        this.saveStatus = 'success';
        this.setSharedWith?.(this.underwritingId, sharedWith);
        this.closeCallback = () => {
          this.events.dispatchEvent(new ShareAccessEvent());
        };
      });
      this.startClosing();
    } catch (err) {
      console.error(err);
      runInAction(() => {
        this.saveStatus = 'error';
      });
    }
  };
}
